/**
 * ============================================================================
 * TRANSIENT: Appointment Config Page (appointment-config-page)
 * Type: poc
 * Amplitude key: none
 * End date: 2026-12-31
 * PR: #TODO_AFTER_PR_CREATION_appointment-config-page
 * ============================================================================
 */

import { Box, Divider, Stack, Typography } from '@evaneos/b2b-ui';
import { differenceInMinutes } from 'date-fns';

import {
    buildGoogleOAuthUrl,
    getPhoneNumberForMarket,
} from '../../resources/appointment/operations';

import { AppointmentContactPartial } from './partials/AppointmentContactPartial';
import { AppointmentDisconnectedStatePartial } from './partials/AppointmentDisconnectedStatePartial';
import { AppointmentGoogleConnectionPartial } from './partials/AppointmentGoogleConnectionPartial';
import { AppointmentPageHeaderPartial } from './partials/AppointmentPageHeaderPartial';
import { AppointmentParamsPartial } from './partials/AppointmentParamsPartial';
import { AppointmentSidebarPartial } from './partials/AppointmentSidebarPartial';
import { AppointmentSlotsPartial } from './partials/AppointmentSlotsPartial';
import { AppointmentWipContentPartial } from './partials/AppointmentWipContentPartial';
import { useAppointment } from './useAppointment';

export const AppointmentPage = (): JSX.Element | null => {
    const {
        isLoading,
        isConnected,
        activeTab,
        days,
        defaultCallType,
        slotDurationInMinutes,
        travelerReminder,
        timezone,
        activeSection,
        activeDays,
        hasNoSlots,
        lastSavedAt,
        slotsRef,
        contactRef,
        paramsRef,
        setActiveTab,
        setDefaultCallType,
        setTravelerReminder,
        setTimezone,
        handleToggleDay,
        handleSlotStartChange,
        handleSlotEndChange,
        handleDeleteSlot,
        handleAddSlot,
        handleDurationChange,
        handleSectionClick,
    } = useAppointment();

    if (isLoading) return null;

    const diffMin = differenceInMinutes(new Date(), lastSavedAt);
    // @todo translate: appointment.savedAt.justNow / appointment.savedAt.minutesAgo
    const savedLabel = diffMin < 1 ? "Sauvegardé à l'instant" : `Sauvegardé il y a ${diffMin} min`;

    return (
        <Box
            data-spec-id="agenda-page"
            sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
            <AppointmentPageHeaderPartial
                activeTab={activeTab}
                isConnected={isConnected}
                onTabChange={setActiveTab}
            />

            <Box sx={{ flex: 1, bgcolor: 'var(--color-beige)', overflow: 'auto' }}>
                {!isConnected && (
                    <AppointmentDisconnectedStatePartial
                        onConnect={() => window.location.assign(buildGoogleOAuthUrl())}
                    />
                )}
                {isConnected && activeTab === 'rendez-vous' && <AppointmentWipContentPartial />}
                {isConnected && activeTab === 'disponibilites' && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '306px 1fr',
                            gap: 3,
                            p: 4,
                            alignItems: 'start',
                        }}
                    >
                        <AppointmentSidebarPartial
                            activeSection={activeSection}
                            activeDays={activeDays}
                            defaultCallType={defaultCallType}
                            slotDurationInMinutes={slotDurationInMinutes}
                            travelerReminder={travelerReminder}
                            onSectionClick={handleSectionClick}
                        />
                        <Box
                            sx={{
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 3,
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    data-spec-id="saved-indicator"
                                >
                                    {savedLabel}
                                </Typography>
                            </Box>
                            <Stack spacing={6} divider={<Divider />}>
                                <Box id="slots" ref={slotsRef}>
                                    <AppointmentSlotsPartial
                                        days={days}
                                        timezone={timezone}
                                        hasNoSlots={hasNoSlots}
                                        onToggleDay={handleToggleDay}
                                        onSlotStartChange={handleSlotStartChange}
                                        onSlotEndChange={handleSlotEndChange}
                                        onDeleteSlot={handleDeleteSlot}
                                        onAddSlot={handleAddSlot}
                                        onTimezoneChange={setTimezone}
                                    />
                                </Box>
                                <Box id="contact" ref={contactRef}>
                                    <AppointmentContactPartial
                                        value={defaultCallType}
                                        onChange={setDefaultCallType}
                                        // @todo: replace 'FR' with agent's actual market code once exposed at agent/agency level
                                        phoneNumber={getPhoneNumberForMarket('FR')}
                                    />
                                </Box>
                                <Box id="params" ref={paramsRef}>
                                    <AppointmentParamsPartial
                                        slotDurationInMinutes={slotDurationInMinutes}
                                        travelerReminder={travelerReminder}
                                        onDurationChange={handleDurationChange}
                                        onTravelerReminderChange={setTravelerReminder}
                                    />
                                </Box>
                                <Box>
                                    <AppointmentGoogleConnectionPartial />
                                </Box>
                            </Stack>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
